import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@shared/lib/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-md hover:bg-primary/90 hover:shadow-lg active:shadow-sm',
        destructive:
          'bg-destructive text-destructive-foreground shadow-md hover:bg-destructive/90 hover:shadow-lg active:shadow-sm focus-visible:ring-destructive/30 dark:bg-destructive/80 dark:hover:bg-destructive/70',
        outline:
          'border-2 border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground hover:border-accent-foreground/20 hover:shadow-md dark:bg-background/50 dark:border-border dark:hover:bg-accent/50 dark:hover:border-accent-foreground/30',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 hover:shadow-md active:shadow-sm',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/30',
        link: 'text-primary underline-offset-4 hover:underline font-medium',
      },
      size: {
        default: 'h-10 pointer-coarse:min-h-11 px-5 py-2.5 has-[>svg]:px-4',
        sm: 'h-9 pointer-coarse:min-h-11 rounded-md gap-1.5 px-4 has-[>svg]:px-3',
        lg: 'h-11 rounded-md px-7 text-base has-[>svg]:px-5',
        icon: 'size-10 pointer-coarse:min-h-11 pointer-coarse:min-w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  type = 'button',
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  const componentProps: Record<string, unknown> = {
    'data-slot': 'button',
    ...props,
    className: cn(buttonVariants({ variant, size, className })),
  };

  if (!asChild) {
    (componentProps as React.ComponentProps<'button'>).type = type;
  }

  return <Comp {...componentProps} />;
}

export { Button, buttonVariants };
